import {
  Event,
  EventCallable,
  createEvent,
  createStore,
  sample,
  attach,
  createEffect,
  StoreWritable,
  combine,
  Store,
  restore
} from 'effector'
import { persist } from 'effector-storage/local'
import { getIsMobile } from '../hooks/use-is-mobile'
import { Permission } from './permission'

type GetDeviceStreamFx = {
  deviceEnabled: boolean
  wantEnabled: boolean
  selectedDevice: MediaDeviceInfo | null
}

export class ToggleableUiElement {
  readonly name: string

  readonly $deviceEnabled: StoreWritable<boolean>

  readonly disableDevice: EventCallable<void> = createEvent<void>()
  readonly enableDevice: EventCallable<void> = createEvent<void>()
  readonly toggle: EventCallable<void> = createEvent<void>()

  constructor(
    name: string,
    restoreUserValues?: Event<void>,
    enabledByDefault = false
  ) {
    this.name = name
    this.$deviceEnabled = createStore<boolean>(enabledByDefault)

    sample({
      clock: this.enableDevice,
      fn: () => true,
      target: this.$deviceEnabled
    })

    sample({
      clock: this.disableDevice,
      fn: () => false,
      target: this.$deviceEnabled
    })

    // toggle переключает только намерение
    sample({
      clock: this.toggle,
      source: this.$deviceEnabled,
      target: createEffect((wasWant: boolean) => {
        if (wasWant) this.disableDevice()
        else this.enableDevice()
      })
    })

    persist({
      store: this.$deviceEnabled,
      key: `${name}_device_enabled`,
      pickup: restoreUserValues
    })
  }
}

export class ToggleableDevice extends ToggleableUiElement {
  readonly name: PermissionName
  private readonly autoSelectDevice = createEvent<MediaDeviceInfo>()
  readonly selectDevice = createEvent<MediaDeviceInfo>()
  readonly setAvailableDevices = createEvent<Array<MediaDeviceInfo>>()
  readonly destroy: EventCallable<void> = createEvent<void>()

  readonly $stream = createStore<MediaStream | null>(null)
  readonly $selectedDevice: StoreWritable<MediaDeviceInfo | null>

  readonly $isWantEnabled = createStore<boolean>(false)
  readonly $availableDevices = createStore<Array<MediaDeviceInfo>>([])
  private readonly setWantEnabled = createEvent<void>()
  private readonly setWantDisabled = createEvent<void>()

  readonly $isEnabled: Store<boolean>

  readonly setIsReady = createEvent<boolean>()
  readonly $isReady = restore(this.setIsReady, false)

  readonly lazyToggle = createEvent<void>()
  readonly restoreUserValues: EventCallable<void>

  constructor(
    name: PermissionName,
    private readonly permission: Permission
  ) {
    const restoreUserValues = createEvent<void>()
    super(name, restoreUserValues)

    this.restoreUserValues = restoreUserValues
    this.$selectedDevice = createStore<MediaDeviceInfo | null>(null)
    this.name = name

    this.$isEnabled = combine(
      {
        deviceEnabled: this.$deviceEnabled,
        wantEnabled: this.$isWantEnabled,
        stream: this.$stream
      },
      ({ deviceEnabled, wantEnabled, stream }) => {
        return !!deviceEnabled && !!wantEnabled && !!stream
      }
    )

    this.attachPermissionsLogic()
    this.attachTrackToggleLogic()
    this.attachLazyToggleLogic()
    this.attachSelectDeviceLogic()
    this.attachStreamLogic()
  }

  private attachPermissionsLogic = (): void => {
    // const fetchAvailableDevicesFx = createEffect(
    //   async (): Promise<Array<MediaDeviceInfo>> => {
    //     const devices = await navigator.mediaDevices.enumerateDevices()
    //
    //     return devices.filter((d) => {
    //       if (this.name === 'camera') return d.kind === 'videoinput'
    //       if (this.name === 'microphone') return d.kind === 'audioinput'
    //       return false
    //     })
    //   }
    // )

    sample({
      clock: this.permission.$state,
      fn: (nSt) => nSt === 'granted',
      target: [this.$deviceEnabled]
    })

    // sample({
    //   source: fetchAvailableDevicesFx.doneData,
    //   target: this.$availableDevices
    // })
    //
    // sample({
    //   source: fetchAvailableDevicesFx.doneData,
    //   filter: (data) => !!data.length,
    //   fn: (data) => data[1],
    //   target: this.$selectedDevice
    // })
  }

  private attachTrackToggleLogic = (): void => {
    // enable device is superior over want-enable,
    //  but we should only track user actions, not persists, etc.
    sample({
      clock: this.disableDevice,
      target: this.setWantDisabled
    })
    sample({
      clock: this.enableDevice,
      target: this.setWantEnabled
    })

    sample({
      clock: this.setWantDisabled,
      fn: () => false,
      target: this.$isWantEnabled
    })

    sample({
      clock: this.setWantEnabled,
      fn: () => true,
      target: this.$isWantEnabled
    })

    sample({
      clock: this.$isWantEnabled,
      source: this.$deviceEnabled,
      filter: Boolean,
      target: attach({
        source: {
          stream: this.$stream,
          isTrackEnabled: this.$isWantEnabled
        },
        effect: ({ stream, isTrackEnabled }) => {
          if (!stream) return

          stream.getTracks().forEach((track) => {
            track.enabled = isTrackEnabled
          })
        }
      })
    })
  }

  private attachLazyToggleLogic = (): void => {
    sample({
      clock: this.lazyToggle,
      target: attach({
        source: {
          deviceEnabled: this.$deviceEnabled,
          isNowEnabled: this.$isEnabled
        },
        effect: ({ isNowEnabled, deviceEnabled }) => {
          if (deviceEnabled) {
            if (isNowEnabled) {
              this.setWantDisabled()
            } else {
              this.setWantEnabled()
            }
          } else {
            if (isNowEnabled) {
              this.disableDevice()
            } else {
              this.enableDevice()
            }
          }
        }
      })
    })

    persist({
      pickup: this.restoreUserValues,
      key: `${this.name}_device_want_enabled`,
      store: this.$isWantEnabled
    })
  }

  private attachSelectDeviceLogic = (): void => {
    sample({
      clock: [this.autoSelectDevice, this.selectDevice],
      target: this.$selectedDevice
    })

    persist({
      store: this.$selectedDevice,
      key: `${this.name}_selected_device`,
      pickup: this.restoreUserValues
    })

    sample({
      clock: this.setAvailableDevices,
      target: this.$availableDevices
    })
  }

  private attachStreamLogic = (): void => {
    const getDeviceStreamFx = attach({
      source: {
        deviceEnabled: this.$deviceEnabled,
        wantEnabled: this.$isWantEnabled,
        selectedDevice: this.$selectedDevice
      },
      effect: this.getDeviceStreamFxImpl
    })

    const stopDeviceStreamFx = attach({
      source: { stream: this.$stream },
      effect: ({ stream }): null => {
        stream?.getTracks().forEach((track) => track.stop())
        return null
      }
    })

    sample({
      clock: [this.$deviceEnabled, this.selectDevice],
      target: [stopDeviceStreamFx, getDeviceStreamFx]
    })

    sample({
      clock: getDeviceStreamFx.doneData,
      target: this.$stream
    })

    sample({
      clock: this.$stream,
      source: this.$deviceEnabled,
      filter: (deviceEnabled, stream) => !!deviceEnabled !== !!stream,
      fn: (_, stream) => !!stream,
      target: this.$deviceEnabled
    })

    sample({
      clock: this.destroy,
      fn: () => false,
      target: [stopDeviceStreamFx, this.$deviceEnabled]
    })
  }

  private getDeviceStreamFxImpl = async ({
    deviceEnabled,
    wantEnabled,
    selectedDevice
  }: GetDeviceStreamFx): Promise<MediaStream | null> => {
    this.setIsReady(false)

    if (window.isDevMode) {
      console.log(
        `try get device media stream ${this.name}, deviceEnabled ${deviceEnabled}, wantEnabled ${wantEnabled}, selectedDevice ${selectedDevice?.label}`
      )
    }

    if (!deviceEnabled) return null

    const deviceId = selectedDevice?.deviceId

    const constraints: MediaStreamConstraints = {}
    if (this.name === 'camera') {
      constraints.video = {
        deviceId: deviceId ? { exact: deviceId } : void 0,
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: getIsMobile()
          ? { ideal: 15, max: 24 }
          : { ideal: 30, max: 60 }
      }
    }
    if (this.name === 'microphone') {
      constraints.audio = {
        deviceId: deviceId ? { exact: deviceId } : void 0,
        channelCount: 1,
        sampleRate: 16384,
        sampleSize: 32,
        echoCancellation: true,
        noiseSuppression: true
      }
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)

      const [track] =
        this.name === 'camera'
          ? mediaStream.getVideoTracks()
          : mediaStream.getAudioTracks()

      if (!track) return null

      const { deviceId } = track.getSettings()
      if (!deviceId) return null

      const devices = await navigator.mediaDevices.enumerateDevices()
      const matched = devices.find((d) => d.deviceId === deviceId)

      if (!matched) return null

      this.autoSelectDevice(matched)

      this.setAvailableDevices(
        devices.filter((d) => {
          if (this.name === 'camera') return d.kind === 'videoinput'
          if (this.name === 'microphone') return d.kind === 'audioinput'
          return false
        })
      )

      if (window.isDevMode) {
        console.log('got device media stream', this.name, 'device', matched)
      }

      mediaStream?.getTracks().forEach((track): void => {
        track.enabled = wantEnabled
      })

      this.setIsReady(true)

      return mediaStream
    } catch (e) {
      console.error(e)
      return null
    }
  }
}
